import { EraQL } from '@cotera/era';
import { err, ok, Result } from 'neverthrow';
import { Assert } from '@cotera/utilities';
import { resolveRel } from './resolve-rel';
import {
  COMPARE_FUNCS,
  GraphDep,
  GraphError,
  GraphErrorResult,
  GraphNode,
  GraphResult,
  IToolResolver,
  Item,
  PartialSuccess,
  Resolution,
  ResolutionOrder,
} from './types';
import { ParseCtxFor } from './eraql-expr-checker';
import { GRAPH_CONSTANTS } from './special-forms';
import { resolveTool } from './resolve-tool';
import { resolveAgent } from './resolve-agent';
import { DocumentEditor } from '../document';
import { BuildExplorer } from '../commit/build-explorer';
import { toolCallSpecialForm } from '../../workflow/tool-call-special-form';
import { agentSpecialForm } from '../../workflow/agent-special-form';
import { Constant } from '@cotera/era';

/**
 * Extract document references from a TiptapDocument source.
 * Uses EditorState.getDependencies() to find all dependencies including:
 * - Mentions (from mention nodes, excluding tool and column mentions)
 * - Symbol references in expression nodes
 * - Symbol references in attachment nodes (when not freehand)
 * Note: Uses empty graph since we don't need recursive extraction here.
 * Note: Tool mentions are excluded because tools are resolved via toolResolver.
 * Note: Column mentions are excluded because they reference columns within the same relation.
 */
const extractDocumentRefs = (
  source: DocumentEditor.DocumentState
): string[] => {
  const editor = new DocumentEditor.EditorState(source);
  // getDependencies() returns symbol references (excluding tools and columns which are resolved differently)
  return editor.getDependencies(BuildExplorer.empty());
};

export const resolve = (
  input: Record<string, GraphNode>,
  opts: {
    readonly resolutionOrder?: ResolutionOrder;
    readonly toolResolver: IToolResolver;
  }
): Record<string, GraphResult> => {
  const graph: Record<string, GraphResult> = {};
  const { resolutionOrder = 'alphabetical', toolResolver } = opts;

  const resolveSym = (
    sym: string,
    resolveStack: readonly string[]
  ): GraphResult | null => {
    const existing = graph[sym];

    if (existing) {
      return existing;
    }

    const def = input[sym];

    if (!def) {
      return null;
    }

    const deps: Record<string, GraphDep[]> = {};

    const ctxFor: ParseCtxFor = (
      location,
      { bindings, attributes }
    ): EraQL.ParseCtx => {
      const lookup =
        (opts?: { stripLeading: string }): EraQL.SymbolLookup =>
        (
          rawSymbol: string,
          meta: EraQL.TokenMeta
        ): Result<EraQL.Item, readonly EraQL.ParseError[]> | null => {
          const { stripLeading = '' } = opts ?? {};
          if (!rawSymbol.startsWith(stripLeading)) {
            return null;
          }
          const lookupSym = rawSymbol.slice(stripLeading.length);

          if (sym === lookupSym || resolveStack.includes(lookupSym)) {
            const e: GraphError = {
              t: 'cyclical-dependency',
              stack: resolveStack,
            };
            graph[sym] ??= err(new GraphErrorResult(e, null));
            return err([{ t: 'custom', msg: 'Cyclical Dependency' }]);
          }

          const res = resolveSym(lookupSym, [lookupSym, ...resolveStack]);

          if (res === null) {
            graph[sym] ??= err(
              new GraphErrorResult(
                {
                  t: 'missing-dependency',
                  name: lookupSym,
                  position: meta,
                },
                null
              )
            );
            return null;
          }

          (deps[lookupSym] ??= []).push({ ...meta, location });

          if (res.isOk()) {
            const item = res.value.item;
            if (item.t === 'tool') {
              // Convert tool to EraQL special-form item
              // Use the toolResolver to get tool info including types
              const toolKey = item.def.type;
              const toolInfo = toolResolver.getToolInfo(toolKey);

              if (toolInfo === null) {
                const pe: EraQL.ParseError = {
                  t: 'custom',
                  msg: `Tool "${lookupSym}" not found. Ensure the tool is registered.`,
                };
                return err([pe]);
              }

              const sp = toolCallSpecialForm(
                lookupSym,
                toolInfo.inputTy,
                toolInfo.outputTy
              );
              const eraqlItem: EraQL.Item = {
                t: 'special-form',
                sp,
                meta: {
                  toolId: item.def.toolId,
                  toolName: item.def.type,
                },
              };
              return ok(eraqlItem);
            } else if (item.t === 'agent') {
              //TODO this special form is never used, so we should figure out how to factor this out
              //as yet unsure of the consequences if it was just removed

              // Convert agent to EraQL special-form item
              // Build tool symbol map from available tools using the resolver
              // Tools are now external (not in graph), so we use the resolver directly
              // this special form is never used, so the map does not matter
              const toolSymbolMap = {};

              const sp = agentSpecialForm({ tools: toolSymbolMap });
              const eraqlItem: EraQL.Item = {
                t: 'special-form',
                sp,
              };
              return ok(eraqlItem);
            } else if (item.t === 'document') {
              // Convert document to EraQL expression representing its markdown content
              const editor = new DocumentEditor.EditorState(
                item.documentNode.source
              );
              const markdown = editor.toPromptMarkdown(
                BuildExplorer.empty(),
                'expression'
              );
              // Create a string constant expression from the document content
              const expr = Constant(markdown, { ty: 'string' });
              const eraqlItem: EraQL.Item = {
                t: 'expr',
                expr,
              };
              return ok(eraqlItem);
            } else {
              return ok(item);
            }
          } else {
            const e: GraphError = {
              t: 'dep-has-error',
              dep: lookupSym,
              position: meta,
            };
            graph[sym] ??= err(new GraphErrorResult(e, null));
            return err([
              { t: 'custom', msg: `Dependency "${lookupSym}" has an error` },
            ]);
          }
        };

      const ctx: EraQL.ParseCtx = {
        dynamicSymbolLookup: lookup({ stripLeading: '%' }),
        attributes,
        bindings: {
          ...bindings,
          ...GRAPH_CONSTANTS,
          '%': { t: 'symbol-lookup', lookup: lookup() },
        },
      };
      return ctx;
    };

    let itemRes: Result<
      Item,
      { err: GraphError; partial: PartialSuccess | null }
    >;

    switch (def.t) {
      case 'expr': {
        itemRes = EraQL.parseExpr(
          def.source,
          ctxFor('source', {
            bindings: {},
            attributes: {},
          })
        )
          .mapErr((errs): { err: GraphError; partial: null } => ({
            err: { t: 'eraql-errors', errs, source: def.source },
            partial: null,
          }))
          .andThen((expr) => {
            if (expr.isAggregated) {
              const e: GraphError = { t: 'cant-be-aggregated' };
              return err({ err: e, partial: null });
            }
            if (expr.isWindowed) {
              const e: GraphError = { t: 'cant-be-windowed' };
              return err({ err: e, partial: null });
            }
            const item: Item = { t: 'expr' as const, expr, exprNode: def };
            return ok(item);
          });
        break;
      }
      case 'document': {
        // Extract and resolve document references
        const documentRefs = extractDocumentRefs(def.source);

        for (const refSym of documentRefs) {
          // Resolve the dependency
          const refRes = resolveSym(refSym, [...resolveStack, sym]);

          if (!refRes) {
            // Symbol not found - will be caught during build
            continue;
          }

          // Track the dependency
          (deps[refSym] ??= []).push({
            range: [0, 0] as const,
            location: 'source',
          });
        }

        // Documents don't need parsing, just pass through
        const item: Item = { t: 'document' as const, documentNode: def };
        itemRes = ok(item);
        break;
      }
      case 'rel': {
        itemRes = resolveRel(def, {
          ctxFor,
          resolutionOrder,
          toolResolver,
        });
        break;
      }
      case 'agent': {
        // Extract and resolve agent editorState references
        const agentRefs = extractDocumentRefs(def.editorState);

        for (const refSym of agentRefs) {
          // Resolve the dependency
          const refRes = resolveSym(refSym, [...resolveStack, sym]);

          if (!refRes) {
            // Symbol not found - will be caught during build
            continue;
          }

          // Track the dependency
          (deps[refSym] ??= []).push({
            range: [0, 0] as const,
            location: 'source',
          });
        }

        itemRes = resolveAgent(def, {
          toolResolver,
          ctxFor,
          resolutionOrder,
        });
        break;
      }
      case 'tool': {
        itemRes = resolveTool(def);
        break;
      }
      default:
        return Assert.unreachable(def);
    }

    if (itemRes.isOk()) {
      const res: Resolution = {
        item: itemRes.value,
        deps,
      };
      graph[sym] ??= ok(res);
      return ok(res);
    } else {
      graph[sym] ??= err(
        new GraphErrorResult(itemRes.error.err, itemRes.error.partial)
      );
      return err(
        new GraphErrorResult(itemRes.error.err, itemRes.error.partial)
      );
    }
  };

  Object.keys(input)
    .sort((a, b) => COMPARE_FUNCS[resolutionOrder](a, b))
    .forEach((name) => {
      const result = resolveSym(name, [])!;
      graph[name] ??= result;
    });

  return graph;
};
